/**
 * AiChat - Reusable chat interface for interacting with AI/LLM endpoints.
 *
 * Features:
 * - Chat message history (user + assistant)
 * - Markdown rendering for assistant responses
 * - Animated typing indicator (bouncing dots)
 * - Model / timing metadata as pill chips
 * - Quick-start suggestion chips on empty state
 * - Staggered message fade-in animations
 * - Hover-reveal copy action
 * - Configurable send handler for different backends
 * - Auto-scroll to latest message
 */

// react
import { useCallback, useEffect, useRef, useState, KeyboardEvent } from 'react';

// mui
import {
	alpha,
	Avatar,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AutoAwesome as IconAI,
	ContentCopy as IconCopy,
	Check as IconCheck,
	NoteAdd as IconNewChat,
	Person as IconUser,
	Send as IconSend,
	Stop as IconStop,
} from '@mui/icons-material';

// markdown
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Types

export interface ChatMessage {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	timestamp: Date;
	/** Metadata returned by the LLM (model, duration, etc.) */
	meta?: {
		model?: string;
		duration_ms?: number;
		service?: string;
	};
}

export interface LlmHistoryMessage {
	role: 'system' | 'user' | 'assistant';
	content: string;
}

export interface AiChatProps {
	/**
	 * Async function that sends a user message and returns the assistant response.
	 * Receives the full conversation history so the backend can maintain context.
	 * Pass an `onChunk` callback to enable real-time streaming; each token will
	 * be appended to the assistant bubble as it arrives.
	 */
	onSend: (params: {
		message: string;
		history: LlmHistoryMessage[];
		onChunk?: (chunk: string) => void;
	}) => Promise<{ response: string; meta?: ChatMessage['meta'] }>;
	/** Callback to abort/stop the current generation */
	onStop?: () => void;
	/** Callback to start a new chat — clears all messages */
	onNewChat?: () => void;
	/** Placeholder text for the input field */
	placeholder?: string;
	/** Optional welcome message shown before any conversation */
	welcomeMessage?: string;
	/** Title shown in the header area */
	title?: string;
	/** Subtitle / description */
	subtitle?: string;
	/** Optional max-height for the chat container (default: 100%) */
	maxHeight?: string | number;
	/** Quick-start suggestion prompts displayed in the empty state */
	suggestions?: string[];
	/** When true the typing indicator shows "Streaming…" instead of "Thinking…" */
	streaming?: boolean;
	/** Called when loading state changes (message in-flight) */
	onLoadingChange?: (isLoading: boolean) => void;
	/** Called when an assistant response is received */
	onResponse?: () => void;
	/** When true, input and suggestions are disabled (e.g. insights still loading) */
	disabled?: boolean;
	/** Message shown when disabled (e.g. "Loading document insights…") */
	disabledMessage?: string;
	/** Initial messages to seed the chat with (e.g. restored from a parent cache) */
	initialMessages?: ChatMessage[];
	/** Notified whenever the message list changes — use to persist outside the component */
	onMessagesChange?: (messages: ChatMessage[]) => void;
	/**
	 * When provided, syncs into local state whenever the list grows beyond what's
	 * currently shown. Useful for picking up answers from a request that was
	 * started in another tab while this component was unmounted.
	 */
	syncMessages?: ChatMessage[];
	/**
	 * Show the typing indicator even when no local send is in flight — e.g. when
	 * a request was started before this component mounted.
	 */
	externalIsLoading?: boolean;
}
// (duplicated interface removed — see above)

// Constants

const AI_PURPLE = '#7c3aed';

const DEFAULT_SUGGESTIONS = [
	'Summarize the latest contract documents',
	'What is the current spend vs budget?',
	'List overdue payment conditions',
	'Show project activity status',
];

// Keyframe animations (injected once)

const BOUNCE_KEYFRAMES = `
@keyframes aiChatBounce {
	0%, 80%, 100% { transform: translateY(0); }
	40% { transform: translateY(-6px); }
}
@keyframes aiChatFadeSlideIn {
	from { opacity: 0; transform: translateY(8px); }
	to   { opacity: 1; transform: translateY(0); }
}
`;

let stylesInjected = false;
const injectStyles = () => {
	if (stylesInjected) return;
	const style = document.createElement('style');
	style.textContent = BOUNCE_KEYFRAMES;
	document.head.appendChild(style);
	stylesInjected = true;
};

// Helpers

let msgIdCounter = 0;
const nextMsgId = () => `msg-${++msgIdCounter}-${Date.now()}`;

/** Read user photo from session (same source as the FundsChain navbar). */
const getSessionUser = (): { photo?: string; firstName?: string; lastName?: string } | null => {
	try {
		const raw = sessionStorage.getItem('user');
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

// Sub-components

/** Animated bouncing dots typing indicator */
const TypingDots = () => (
	<Stack direction="row" spacing={0.5} sx={{ py: 0.5, px: 0.25 }}>
		{[0, 1, 2].map((i) => (
			<Box
				key={i}
				sx={{
					width: 8,
					height: 8,
					borderRadius: '50%',
					bgcolor: AI_PURPLE,
					opacity: 0.6,
					animation: `aiChatBounce 1.2s ease-in-out infinite`,
					animationDelay: `${i * 0.15}s`,
				}}
			/>
		))}
	</Stack>
);

// Component

export const AiChat = ({
	onSend,
	onStop,
	onNewChat,
	placeholder = 'Ask me anything...',
	welcomeMessage = "Hello! I'm your AI assistant. How can I help you today?",
	title,
	subtitle,
	maxHeight = '100%',
	suggestions = DEFAULT_SUGGESTIONS,
	streaming = false,
	onLoadingChange,
	onResponse,
	disabled = false,
	disabledMessage = 'Loading…',
	initialMessages,
	onMessagesChange,
	syncMessages,
	externalIsLoading = false,
}: AiChatProps) => {
	const theme = useTheme();

	// Session user (for avatar photo — same source as the FundsChain navbar)
	const sessionUser = getSessionUser();

	// Inject keyframe styles on mount
	useEffect(() => {
		injectStyles();
	}, []);

	// State
	const [messages, setMessages] = useState<ChatMessage[]>(() => initialMessages ?? []);
	const [input, setInput] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [copiedId, setCopiedId] = useState<string | null>(null);

	// Notify parent on every change so it can persist outside this component's lifetime
	useEffect(() => {
		onMessagesChange?.(messages);
	}, [messages, onMessagesChange]);

	// Adopt externally-provided messages when they extend or differ from current.
	// Skips while a local send is mid-flight (we have a streaming placeholder)
	// to avoid clobbering live token-by-token updates.
	useEffect(() => {
		if (!syncMessages) return;
		const hasLocalPlaceholder =
			messages.length > 0 &&
			messages[messages.length - 1].role === 'assistant' &&
			messages[messages.length - 1].content === '';
		if (hasLocalPlaceholder) return;

		// Adopt when length grew or the tail message changed.
		const grew = syncMessages.length > messages.length;
		const tailLocal = messages[messages.length - 1];
		const tailSync = syncMessages[syncMessages.length - 1];
		const tailDiffers =
			syncMessages.length === messages.length &&
			tailLocal &&
			tailSync &&
			(tailLocal.id !== tailSync.id || tailLocal.content !== tailSync.content);

		if (grew || tailDiffers) {
			setMessages(syncMessages);
		}
	}, [syncMessages, messages]);

	// New Chat handler — clears conversation and invokes optional callback
	const handleNewChat = useCallback(() => {
		setMessages([]);
		setInput('');
		setIsLoading(false);
		setCopiedId(null);
		onNewChat?.();
		setTimeout(() => inputRef.current?.focus(), 100);
	}, [onNewChat]);

	// Refs
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Auto-scroll to bottom
	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, []);

	// Effective loading state — either a local send or a background one started
	// before this component mounted (tab switch mid-generation).
	const effectiveIsLoading = isLoading || externalIsLoading;

	useEffect(() => {
		scrollToBottom();
	}, [messages, effectiveIsLoading, scrollToBottom]);

	// Send message handler
	const sendMessage = useCallback(
		async (text: string) => {
			const trimmed = text.trim();
			if (!trimmed || effectiveIsLoading) return;

			const userMsg: ChatMessage = {
				id: nextMsgId(),
				role: 'user',
				content: trimmed,
				timestamp: new Date(),
			};

			// Snapshot conversation history BEFORE adding the new user turn
			const history: LlmHistoryMessage[] = messages.map((m) => ({
				role: m.role as LlmHistoryMessage['role'],
				content: m.content,
			}));

			setMessages((prev) => [...prev, userMsg]);
			setInput('');
			setIsLoading(true);
			onLoadingChange?.(true);

			// Insert an empty assistant placeholder immediately — will fill via chunks or final response
			const assistantId = nextMsgId();
			setMessages((prev) => [
				...prev,
				{ id: assistantId, role: 'assistant', content: '', timestamp: new Date() },
			]);

			try {
				const onChunk = (chunk: string) => {
					setMessages((prev) =>
						prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunk } : m))
					);
				};

				const { response, meta } = await onSend({ message: trimmed, history, onChunk });

				// For non-streaming handlers that return the full response at once, fill it in now
				setMessages((prev) =>
					prev.map((m) =>
						m.id === assistantId
							? {
									...m,
									// If streaming already populated content, keep it; otherwise use response
									content: m.content || response,
									meta,
								}
							: m
					)
				);
				onResponse?.();
			} catch (err) {
				// If aborted, show a clean "stopped" message instead of an error
				const isAborted = err instanceof DOMException && err.name === 'AbortError';
				setMessages((prev) =>
					prev.map((m) =>
						m.id === assistantId
							? {
									...m,
									content: isAborted
										? '*Generation stopped.*'
										: `**Error:** ${
												err instanceof Error
													? err.message
													: 'Something went wrong. Please try again.'
											}`,
								}
							: m
					)
				);
			} finally {
				setIsLoading(false);
				onLoadingChange?.(false);
				setTimeout(() => inputRef.current?.focus(), 100);
			}
		},
		[effectiveIsLoading, messages, onSend, onLoadingChange, onResponse]
	);

	const handleSend = useCallback(() => {
		sendMessage(input);
	}, [input, sendMessage]);

	// Keyboard handler
	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend]
	);

	// Copy message content
	const handleCopy = useCallback((msg: ChatMessage) => {
		navigator.clipboard.writeText(msg.content).then(() => {
			setCopiedId(msg.id);
			setTimeout(() => setCopiedId(null), 2000);
		});
	}, []);

	// =========================================================================
	// Render helpers
	// =========================================================================

	const renderMessage = (msg: ChatMessage, index: number) => {
		const isUser = msg.role === 'user';
		const isCopied = copiedId === msg.id;

		return (
			<Box
				key={msg.id}
				sx={{
					display: 'flex',
					justifyContent: isUser ? 'flex-end' : 'flex-start',
					mb: 3,
					px: { xs: 2, sm: 4 },
					animation: 'aiChatFadeSlideIn 0.3s ease-out',
					animationFillMode: 'backwards',
					animationDelay: `${Math.min(index * 0.05, 0.2)}s`,
				}}>
				<Stack
					direction={isUser ? 'row-reverse' : 'row'}
					spacing={2}
					sx={{ maxWidth: { xs: '90%', sm: '75%', md: '70%' } }}>
					{/* Avatar */}
					<Avatar
						src={isUser ? sessionUser?.photo || undefined : undefined}
						sx={{
							background: isUser
								? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
								: `linear-gradient(135deg, ${AI_PURPLE}, ${alpha(AI_PURPLE, 0.7)})`,
							height: 40,
							width: 40,
							mt: 0.5,
							boxShadow: `0 2px 8px ${alpha(isUser ? theme.palette.primary.main : AI_PURPLE, 0.25)}`,
						}}>
						{isUser ? (
							<IconUser sx={{ fontSize: 20, color: '#fff' }} />
						) : (
							<IconAI sx={{ fontSize: 20, color: '#fff' }} />
						)}
					</Avatar>

					{/* Bubble + meta */}
					<Box
						sx={{
							'&:hover .chat-actions': { opacity: 1 },
						}}>
						<Paper
							elevation={0}
							sx={{
								bgcolor: isUser ? theme.palette.primary.main : '#fff',
								background: isUser
									? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`
									: undefined,
								borderRadius: '20px',
								borderTopRightRadius: isUser ? '4px' : '20px',
								borderTopLeftRadius: !isUser ? '4px' : '20px',
								color: isUser ? '#fff' : theme.palette.text.primary,
								px: 3,
								py: 2,
								border: isUser ? 'none' : `1px solid ${theme.palette.divider}`,
								boxShadow: isUser
									? `0 2px 12px ${alpha(theme.palette.primary.main, 0.3)}`
									: '0 1px 6px rgba(0,0,0,0.06)',
								'& pre': {
									bgcolor: isUser ? alpha('#000', 0.15) : '#f8f9fa',
									border: isUser ? 'none' : '1px solid #e5e7eb',
								},
								'& code': {
									bgcolor: isUser ? alpha('#000', 0.15) : '#f3f4f6',
								},
							}}>
							{isUser ? (
								<Typography
									variant="body1"
									sx={{ whiteSpace: 'pre-wrap', fontSize: '1.35rem', lineHeight: 1.6 }}>
									{msg.content}
								</Typography>
							) : (
								<Box
									component="div"
									sx={{
										fontSize: '1.35rem',
										lineHeight: 1.75,
										'& p': { m: 0, mb: 1 },
										'& p:last-child': { mb: 0 },
										'& ul, & ol': { pl: 3, mb: 1 },
										'& li': { mb: 0.5 },
										'& h1, & h2, & h3, & h4': { mt: 2, mb: 1, fontWeight: 600 },
										'& h1': { fontSize: '1.6rem' },
										'& h2': { fontSize: '1.5rem' },
										'& h3': { fontSize: '1.4rem' },
										'& table': {
											borderCollapse: 'collapse',
											width: '100%',
											my: 1.5,
											fontSize: '1.25rem',
										},
										'& th, & td': {
											border: '1px solid #e5e7eb',
											px: 1.5,
											py: 1,
											textAlign: 'left',
										},
										'& th': {
											bgcolor: '#f8f9fa',
											fontWeight: 600,
										},
										'& pre': {
											bgcolor: '#f5f5f5',
											p: 2,
											borderRadius: '12px',
											overflowX: 'auto',
											my: 1,
											border: '1px solid #e5e7eb',
											fontSize: '1.25rem',
										},
										'& code': {
											bgcolor: '#f0f0f0',
											px: 0.75,
											py: 0.25,
											borderRadius: '6px',
											fontSize: '1.25rem',
											fontWeight: 500,
										},
										'& blockquote': {
											borderLeft: '3px solid #d1d5db',
											pl: 2,
											my: 1,
											color: 'text.secondary',
										},
										'& hr': {
											border: 'none',
											borderTop: '1px solid #e5e7eb',
											my: 2,
										},
										'& a': {
											color: AI_PURPLE,
											textDecoration: 'underline',
										},
									}}>
									<ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
								</Box>
							)}
						</Paper>

						{/* Meta row */}
						<Stack
							direction="row"
							alignItems="center"
							spacing={0.75}
							className="chat-actions"
							sx={{
								mt: 1,
								px: 0.5,
								opacity: 0.6,
								transition: 'opacity 0.2s',
							}}>
							<Typography
								sx={{
									fontSize: '1.25rem',
								}}>
								{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
							</Typography>

							{msg.meta?.model && (
								<Chip
									label={msg.meta.model}
									size="small"
									variant="outlined"
									sx={{
										height: 28,
										fontSize: '1.25rem',
										fontWeight: 500,
										borderColor: alpha(AI_PURPLE, 0.3),
										color: AI_PURPLE,
									}}
								/>
							)}

							{msg.meta?.duration_ms !== undefined && (
								<Typography
									sx={{
										fontSize: '1.25rem',
										color: 'text.disabled',
									}}>
									{msg.meta.duration_ms < 1000
										? `${msg.meta.duration_ms}ms`
										: `${(msg.meta.duration_ms / 1000).toFixed(1)}s`}
								</Typography>
							)}

							{!isUser && (
								<Tooltip title={isCopied ? 'Copied!' : 'Copy'} placement="top">
									<IconButton
										size="small"
										onClick={() => handleCopy(msg)}
										sx={{
											p: 0.5,
											color: isCopied ? theme.palette.success.main : 'text.disabled',
											'&:hover': {
												bgcolor: alpha(AI_PURPLE, 0.08),
												color: AI_PURPLE,
											},
										}}>
										{isCopied ? (
											<IconCheck sx={{ fontSize: 20 }} />
										) : (
											<IconCopy sx={{ fontSize: 20 }} />
										)}
									</IconButton>
								</Tooltip>
							)}
						</Stack>
					</Box>
				</Stack>
			</Box>
		);
	};

	// =========================================================================
	// Main render
	// =========================================================================

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				maxHeight,
				overflow: 'hidden',
				bgcolor: '#fafafa',
			}}>
			{/* Optional Header — skip if page provides its own via HeaderListingPage */}
			{(title || subtitle) && (
				<Box
					sx={{
						borderBottom: `1px solid ${theme.palette.divider}`,
						px: 3,
						py: 2,
					}}>
					<Stack direction="row" alignItems="center" spacing={1.5}>
						<Avatar
							sx={{
								background: `linear-gradient(135deg, ${AI_PURPLE}, ${alpha(AI_PURPLE, 0.7)})`,
								height: 40,
								width: 40,
							}}>
							<IconAI sx={{ color: '#fff' }} />
						</Avatar>
						<Box>
							{title && (
								<Typography
									variant="subtitle1"
									sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '1.35rem' }}>
									{title}
								</Typography>
							)}
							{subtitle && (
								<Typography color="text.secondary" sx={{ fontSize: '1.25rem' }}>
									{subtitle}
								</Typography>
							)}
						</Box>
					</Stack>
				</Box>
			)}

			{/* Messages Area */}
			<Box
				sx={{
					flex: 1,
					overflow: 'auto',
					py: 3,
					// Styled scrollbar
					'&::-webkit-scrollbar': { width: 6 },
					'&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
					'&::-webkit-scrollbar-thumb': {
						bgcolor: alpha(theme.palette.grey[400], 0.4),
						borderRadius: 3,
						'&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.6) },
					},
				}}>
				{/* Empty state — welcome + suggestion chips */}
				{messages.length === 0 && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							gap: 3,
							px: 4,
							textAlign: 'center',
						}}>
						<Avatar
							sx={{
								background: `linear-gradient(135deg, ${AI_PURPLE}, ${alpha(AI_PURPLE, 0.6)})`,
								height: 72,
								width: 72,
								boxShadow: `0 4px 20px ${alpha(AI_PURPLE, 0.25)}`,
							}}>
							<IconAI sx={{ color: '#fff', fontSize: 36 }} />
						</Avatar>
						<Typography
							sx={{
								maxWidth: 500,
								fontSize: '1.35rem',
								color: 'text.secondary',
								lineHeight: 1.6,
							}}>
							{welcomeMessage}
						</Typography>

						{/* Suggestion chips */}
						{suggestions.length > 0 && (
							<Stack
								direction="row"
								flexWrap="wrap"
								justifyContent="center"
								sx={{ gap: 1.5, mt: 1, maxWidth: 600 }}>
								{suggestions.map((s) => (
									<Chip
										key={s}
										label={s}
										variant="outlined"
										clickable={!disabled}
										onClick={disabled ? undefined : () => sendMessage(s)}
										sx={{
											fontSize: '1.25rem',
											fontWeight: 500,
											py: 2.5,
											px: 1,
											borderRadius: '16px',
											borderColor: alpha(AI_PURPLE, 0.25),
											color: theme.palette.text.secondary,
											transition: 'all 0.2s',
											'&:hover': disabled
												? {}
												: {
														bgcolor: alpha(AI_PURPLE, 0.06),
														borderColor: AI_PURPLE,
														color: AI_PURPLE,
													},
											opacity: disabled ? 0.45 : 1,
											cursor: disabled ? 'default' : undefined,
										}}
									/>
								))}
							</Stack>
						)}

						{/* Disabled / loading overlay message */}
						{disabled && (
							<Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2 }}>
								<CircularProgress size={18} sx={{ color: 'white' }} />
								<Typography sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
									{disabledMessage}
								</Typography>
							</Stack>
						)}
					</Box>
				)}

				{/* Message list — hide the empty assistant placeholder while loading */}
				{messages.map((msg, i) => {
					if (
						effectiveIsLoading &&
						msg.role === 'assistant' &&
						msg.content === '' &&
						i === messages.length - 1
					) {
						return null;
					}
					return renderMessage(msg, i);
				})}

				{/* Typing indicator — bouncing dots; hidden once streaming content starts arriving */}
				{effectiveIsLoading &&
					(messages.length === 0 ||
						messages[messages.length - 1]?.content === '' ||
						messages[messages.length - 1]?.role === 'user') && (
						<Box
							sx={{
								display: 'flex',
								px: { xs: 2, sm: 4 },
								mb: 2,
								animation: 'aiChatFadeSlideIn 0.3s ease-out',
							}}>
							<Stack direction="row" spacing={2}>
								<Avatar
									sx={{
										background: `linear-gradient(135deg, ${AI_PURPLE}, ${alpha(AI_PURPLE, 0.7)})`,
										height: 40,
										width: 40,
										mt: 0.5,
										boxShadow: `0 2px 8px ${alpha(AI_PURPLE, 0.25)}`,
									}}>
									<IconAI sx={{ fontSize: 20, color: '#fff' }} />
								</Avatar>
								<Paper
									elevation={0}
									sx={{
										bgcolor: '#fff',
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: '20px',
										borderTopLeftRadius: '4px',
										px: 3,
										py: 2,
										display: 'flex',
										alignItems: 'center',
										gap: 1.5,
										boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
									}}>
									<TypingDots />
									<Typography sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
										{streaming ? 'Streaming…' : 'Thinking…'}
									</Typography>
								</Paper>
							</Stack>
						</Box>
					)}

				<div ref={messagesEndRef} />
			</Box>

			{/* Input Area */}
			<Box sx={{ px: { xs: 2, sm: 4 }, py: 2.5, bgcolor: alpha(theme.palette.grey[50], 0.8) }}>
				<Stack direction="row" spacing={1.5} alignItems="flex-end">
					{/* New Chat button — visible when there are messages */}
					{onNewChat && messages.length > 0 && (
						<Tooltip title="New Chat" placement="top">
							<IconButton
								onClick={handleNewChat}
								disabled={effectiveIsLoading}
								sx={{
									border: '1.5px solid',
									borderColor: alpha(AI_PURPLE, 0.3),
									borderRadius: '50%',
									height: 48,
									width: 48,
									flexShrink: 0,
									mb: 0.25,
									color: AI_PURPLE,
									transition: 'all 0.25s ease',
									'&:hover': {
										bgcolor: alpha(AI_PURPLE, 0.08),
										borderColor: AI_PURPLE,
										transform: 'scale(1.08)',
									},
									'&.Mui-disabled': {
										borderColor: theme.palette.grey[300],
										color: theme.palette.grey[400],
									},
								}}>
								<IconNewChat sx={{ fontSize: 22 }} />
							</IconButton>
						</Tooltip>
					)}
					<Paper
						elevation={0}
						sx={{
							flex: 1,
							display: 'flex',
							alignItems: 'flex-end',
							gap: 1.5,
							px: 2.5,
							py: 1.25,
							borderRadius: '28px',
							border: '1.5px solid',
							borderColor: input.trim() ? theme.palette.primary.main : theme.palette.grey[300],
							bgcolor: '#fff',
							boxShadow: input.trim()
								? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`
								: '0 2px 8px rgba(0,0,0,0.04)',
							transition: 'all 0.2s ease',
							'&:hover': {
								borderColor: alpha(theme.palette.primary.main, 0.5),
								boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
							},
						}}>
						<TextField
							inputRef={inputRef}
							fullWidth
							multiline
							maxRows={4}
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={disabled ? disabledMessage : placeholder}
							disabled={effectiveIsLoading || disabled}
							variant="standard"
							InputProps={{ disableUnderline: true }}
							sx={{
								'& .MuiInputBase-root': {
									fontSize: '1.35rem',
									py: 0.75,
									px: 1,
								},
								'& .MuiInputBase-input::placeholder': {
									fontSize: '1.35rem',
									opacity: 0.45,
								},
							}}
						/>
						{/* Show Stop button while loading, Send button otherwise */}
						{effectiveIsLoading && onStop ? (
							<Tooltip title="Stop generation" placement="top">
								<IconButton
									onClick={onStop}
									sx={{
										background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
										color: '#fff',
										borderRadius: '50%',
										height: 48,
										width: 48,
										flexShrink: 0,
										mb: 0.25,
										transition: 'all 0.25s ease',
										boxShadow: `0 3px 12px ${alpha(theme.palette.error.main, 0.35)}`,
										'&:hover': {
											background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`,
											transform: 'scale(1.08)',
										},
									}}>
									<IconStop sx={{ fontSize: 22 }} />
								</IconButton>
							</Tooltip>
						) : (
							<IconButton
								onClick={handleSend}
								disabled={!input.trim() || effectiveIsLoading || disabled}
								sx={{
									background: input.trim()
										? `linear-gradient(135deg, ${theme.palette.primary.main}, ${AI_PURPLE})`
										: theme.palette.grey[200],
									color: input.trim() ? '#fff' : theme.palette.grey[400],
									borderRadius: '50%',
									height: 48,
									width: 48,
									flexShrink: 0,
									mb: 0.25,
									transition: 'all 0.25s ease',
									boxShadow: input.trim()
										? `0 3px 12px ${alpha(theme.palette.primary.main, 0.35)}`
										: 'none',
									'&:hover': {
										background: input.trim()
											? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${AI_PURPLE})`
											: theme.palette.grey[300],
										transform: input.trim() ? 'scale(1.08)' : 'none',
									},
									'&.Mui-disabled': {
										bgcolor: theme.palette.grey[200],
										color: theme.palette.grey[400],
									},
								}}>
								<IconSend sx={{ fontSize: 22 }} />
							</IconButton>
						)}
					</Paper>
				</Stack>
				<Typography
					color="text.disabled"
					sx={{ mt: 1, display: 'block', textAlign: 'center', fontSize: '1.25rem' }}>
					Press Enter to send &middot; Shift+Enter for new line
				</Typography>
			</Box>
		</Box>
	);
};
